/**
 * TextComponent renders a text label as a billboard sprite that always faces the camera.
 * Creates a canvas-based texture with configurable text, font, colors, and styling.
 * Useful for labels, name tags, debug information, and any text that needs to be visible in 3D space.
 */
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';
import { SceneComponent } from '../SceneComponent.js';

import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { SceneComponentOptions } from '../SceneComponent.js';

export interface TextComponentOptions extends SceneComponentOptions {
  text?: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  textColor?: string;
  backgroundColor?: string;
  backgroundOpacity?: number;
  padding?: number;
  borderRadius?: number;
  canvasWidth?: number;
  canvasHeight?: number;
  depthTest?: boolean;
  depthWrite?: boolean;
}

@EngineClass('TextComponent')
export class TextComponent extends SceneComponent {


  @property()
  public text: string = '';

  @property({ type: 'number', min: 8, max: 128, step: 1 })
  public fontSize: number = 32;

  @property()
  public fontFamily: string = 'Arial';

  @property()
  public fontWeight: string = 'bold';

  @property()
  public textColor: string = '#ffffff';

  @property()
  public backgroundColor: string = '#000000';

  @property({ type: 'number', min: 0, max: 1, step: 0.1, decimals: 2 })
  public backgroundOpacity: number = 0.7;

  @property({ type: 'number', min: 0, max: 100, step: 1 })
  public padding: number = 10;

  @property({ type: 'number', min: 0, max: 50, step: 1 })
  public borderRadius: number = 10;

  @property({ type: 'number', min: 64, max: 2048, step: 64 })
  public canvasWidth: number = 512;

  @property({ type: 'number', min: 32, max: 1024, step: 32 })
  public canvasHeight: number = 128;

  @property()
  public depthTest: boolean = true;

  @property()
  public depthWrite: boolean = false;

  private sprite: THREE.Sprite | null = null;
  private spriteMaterial: THREE.SpriteMaterial | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private texture: THREE.CanvasTexture | null = null;


  public override initialize(options?: TextComponentOptions): void {
    super.initialize(options);

    this.text = options?.text ?? this.text;
    this.fontSize = options?.fontSize ?? this.fontSize;
    this.fontFamily = options?.fontFamily ?? this.fontFamily;
    this.fontWeight = options?.fontWeight ?? this.fontWeight;
    this.textColor = options?.textColor ?? this.textColor;
    this.backgroundColor = options?.backgroundColor ?? this.backgroundColor;
    this.backgroundOpacity = options?.backgroundOpacity ?? this.backgroundOpacity;
    this.padding = options?.padding ?? this.padding;
    this.borderRadius = options?.borderRadius ?? this.borderRadius;
    this.canvasWidth = options?.canvasWidth ?? this.canvasWidth;
    this.canvasHeight = options?.canvasHeight ?? this.canvasHeight;
    this.depthTest = options?.depthTest ?? this.depthTest;
    this.depthWrite = options?.depthWrite ?? this.depthWrite;

    this.createSprite();
  }

  public override postLoad(): void {
    super.postLoad();
    this.createSprite();
  }

  private createSprite(): void {
    // Create canvas for text rendering
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvasWidth;
    this.canvas.height = this.canvasHeight;

    // Create texture and material
    this.texture = new THREE.CanvasTexture(this.canvas);
    // Disable mipmaps and use linear filtering for sharp text
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = true;
    this.spriteMaterial = new THREE.SpriteMaterial({
      map: this.texture,
      transparent: true,
      depthTest: this.depthTest,
      depthWrite: this.depthWrite,
    });

    // Create sprite and scale to match canvas aspect ratio
    this.sprite = new THREE.Sprite(this.spriteMaterial);
    const aspectRatio = this.canvasWidth / this.canvasHeight;
    this.sprite.scale.set(aspectRatio, 1, 1);
    this.add(this.sprite);

    // Initial render
    this.renderText();
  }

  public override beginPlay(): void {
    super.beginPlay();
    // Re-render text in case properties changed before play
    this.renderText();
  }

  public updateText(newText?: string): void {
    if (newText !== undefined) {
      this.text = newText;
    }
    this.renderText();
  }

  private renderText(): void {
    if (!this.canvas || !this.texture) return;

    const context = this.canvas.getContext('2d');
    if (!context) return;

    const width = this.canvas.width;
    const height = this.canvas.height;

    // Clear canvas
    context.clearRect(0, 0, width, height);

    // Draw background with rounded corners
    if (this.backgroundOpacity > 0) {
      // Convert hex color to rgba
      const bgColor = this.hexToRgba(this.backgroundColor, this.backgroundOpacity);
      context.fillStyle = bgColor;
      context.beginPath();
      this.roundRect(context, this.padding, this.padding, width - this.padding * 2, height - this.padding * 2, this.borderRadius);
      context.fill();
    }

    // Set up text rendering
    context.fillStyle = this.textColor;
    context.font = `${this.fontWeight} ${this.fontSize}px ${this.fontFamily}`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // Handle multi-line text
    const lines = this.text.split('\n');
    const lineHeight = this.fontSize * 1.2;
    const totalTextHeight = lines.length * lineHeight;
    const startY = height / 2 - totalTextHeight / 2 + lineHeight / 2;

    lines.forEach((line, index) => {
      context.fillText(line, width / 2, startY + index * lineHeight);
    });

    // Update texture
    this.texture.needsUpdate = true;
  }

  private hexToRgba(hex: string, alpha: number): string {
    // Remove # if present
    hex = hex.replace('#', '');

    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    if (w < 2 * r) r = w / 2;
    if (h < 2 * r) r = h / 2;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);

    // Re-render text when any text-related property changes
    const textProperties = [
      'text', 'fontSize', 'fontFamily', 'fontWeight', 'textColor',
      'backgroundColor', 'backgroundOpacity', 'padding', 'borderRadius'
    ];

    if (textProperties.includes(path)) {
      this.renderText();
    }

    // Handle canvas size changes (requires recreating sprite)
    if (path === 'canvasWidth' || path === 'canvasHeight') {
      if (this.canvas) {
        this.canvas.width = this.canvasWidth;
        this.canvas.height = this.canvasHeight;
        // Update sprite aspect ratio
        if (this.sprite) {
          const aspectRatio = this.canvasWidth / this.canvasHeight;
          this.sprite.scale.set(aspectRatio, 1, 1);
        }
        this.renderText();
      }
    }

    // Handle depth settings
    if ((path === 'depthTest' || path === 'depthWrite') && this.spriteMaterial) {
      this.spriteMaterial.depthTest = this.depthTest;
      this.spriteMaterial.depthWrite = this.depthWrite;
      this.spriteMaterial.needsUpdate = true;
    }
  }

  public override endPlay(): void {
    // Clean up resources
    if (this.texture) {
      this.texture.dispose();
      this.texture = null;
    }
    if (this.spriteMaterial) {
      this.spriteMaterial.dispose();
      this.spriteMaterial = null;
    }
    this.sprite = null;
    this.canvas = null;

    super.endPlay();
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Text';
  }
}
